import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Files, FolderOpen } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import type { Activity } from '@/services/activityService';
import type { PracticeGroup } from './practiceActivityGroupTypes';

export interface PracticeBulkSelectConfig {
  enabled: boolean;
  selectedIds: Set<string>;
  onToggleGroup: (activityIds: string[], checked: boolean) => void;
}

interface PracticeActivityGroupsProps {
  groups: PracticeGroup[];
  expandedPractices: Set<string>;
  onTogglePractice: (practiceId: string) => void;
  renderActivity: (activity: Activity) => React.ReactNode;
  practiceBulkSelect?: PracticeBulkSelectConfig;
}

export default function PracticeActivityGroups({
  groups,
  expandedPractices,
  onTogglePractice,
  renderActivity,
  practiceBulkSelect,
}: PracticeActivityGroupsProps) {
  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const isExpanded = expandedPractices.has(group.practiceId);
        const ids = group.activities.map((a) => a.id);
        const allSelected =
          ids.length > 0 && ids.every((id) => practiceBulkSelect?.selectedIds.has(id));
        const someSelected = ids.some((id) => practiceBulkSelect?.selectedIds.has(id));
        const showBulk = practiceBulkSelect?.enabled === true;

        return (
          <div key={group.practiceId} className="border rounded-lg overflow-hidden">
            <div className="flex w-full items-stretch bg-gray-50 hover:bg-gray-100 transition-colors">
              {showBulk && (
                <div
                  className="flex items-center pl-3 pr-1 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={(v) => {
                      practiceBulkSelect.onToggleGroup(ids, v === true);
                    }}
                    aria-label={
                      allSelected ? 'Deseleziona tutte le attività della pratica' : 'Seleziona tutte le attività della pratica'
                    }
                  />
                </div>
              )}
              <button
                type="button"
                className="flex flex-1 items-center gap-3 px-4 py-3 text-left min-w-0"
                onClick={() => onTogglePractice(group.practiceId)}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
                )}
                <FolderOpen className="h-4 w-4 text-blue-500 shrink-0" />
                {group.practiceNumber && (
                  <span
                    className="text-xs font-mono text-muted-foreground shrink-0"
                    title="Numero pratica"
                  >
                    #{group.practiceNumber}
                  </span>
                )}
                <span className="font-medium text-sm truncate">{group.title}</span>
                {group.clientName && (
                  <span className="text-xs text-gray-500 truncate">— {group.clientName}</span>
                )}
                {group.typeName && (
                  <span className="text-xs text-gray-400 truncate">({group.typeName})</span>
                )}
                <span className="ml-auto shrink-0 flex items-center gap-2">
                  <Link
                    to={`/practices/${group.practiceId}?tab=documents`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-xs font-medium text-foreground hover:bg-accent hover:text-accent-foreground"
                    title="Apri scheda pratica, tab Documenti"
                  >
                    <Files className="h-3.5 w-3.5" />
                    Documenti
                  </Link>
                  <span className="text-xs font-medium bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">
                    {group.activities.length}
                  </span>
                </span>
              </button>
            </div>
            {isExpanded && (
              <div className="p-3 space-y-2 bg-white">
                {group.activities.map((activity) => (
                  <div key={activity.id}>{renderActivity(activity)}</div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
